import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Flame, Clock, BookOpen, Brain, Trophy,
  ChevronRight, Share2, Loader2, X, Twitter, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * WrappedPage — full-screen Spotify-Wrapped-style stat reveal.
 *
 * Why: zero-cost viral acquisition channel. Users tap through 7 stat
 * slides → final slide has Share button → posts to socials. The
 * artifact is intentionally large-typography + bold-color so the
 * screenshot looks great in any feed.
 *
 * Architecture: single source of truth is `data` from /api/wrapped.
 * Each slide is a self-contained `<Slide>` keyed by index so Framer
 * Motion animates entry/exit. Background gradient shifts per slide
 * to feel cinematic.
 */
const SLIDE_COUNT = 7; // intro + 6 stats — keep in sync with buildSlides

const SLIDE_BG = [
  'from-[#002FA7] via-[#001A66] to-[#000814]',
  'from-[#FF6B00] via-[#B83200] to-[#1A0500]',
  'from-[#00C26B] via-[#006B3B] to-[#001A0E]',
  'from-[#7C3AED] via-[#3E1C7A] to-[#0E0820]',
  'from-[#FF3B6B] via-[#9C1F3F] to-[#1A040A]',
  'from-[#FFB800] via-[#7A5800] to-[#1A1200]',
  'from-[#002FA7] via-[#00185E] to-[#0A0A0A]',
];

export default function WrappedPage() {
  const navigate = useNavigate();
  const { year: yearParam } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slide, setSlide] = useState(0);
  const [muted] = useState(true);
  const advanceTimer = useRef(null);

  const targetYear = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();

  // Hooks must be declared *before* any early returns to satisfy
  // rules-of-hooks — slide navigation is stable across renders.
  const next = useCallback(() => {
    setSlide((s) => (s + 1 >= SLIDE_COUNT ? SLIDE_COUNT - 1 : s + 1));
  }, []);
  const back = useCallback(() => {
    setSlide((s) => (s - 1 < 0 ? 0 : s - 1));
  }, []);
  const share = useCallback(async (channel) => {
    if (!data) return;
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      await axios.post(`${API}/wrapped/${targetYear}/share`, { channel },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    } catch { /* analytics is best-effort */ }
    const text = `I focused ${data.focus_hours}h on iStudyPro this year — top ${100 - data.percentile}% of students 🔥`;
    const url = 'https://istudypro.net';
    if (channel === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
    } else if (channel === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
    } else if (channel === 'copy') {
      try {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success('Copied to clipboard!');
      } catch {
        toast.error('Could not copy');
      }
    }
  }, [data, targetYear]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        const res = await axios.get(`${API}/wrapped/${targetYear}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      } catch (e) {
        if (cancelled) return;
        toast.error(e.response?.data?.detail || 'Could not load your Wrapped');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [targetYear]);

  // Auto-advance every 4.5s, but pause on hover/tap
  useEffect(() => {
    if (loading || !data) return;
    advanceTimer.current = setTimeout(() => {
      setSlide((s) => Math.min(s + 1, SLIDES.length - 1));
    }, 4500);
    return () => clearTimeout(advanceTimer.current);
    // SLIDES is a module-scope constant — safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide, loading, data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" />
          <p className="text-sm text-white/60">Crunching your year…</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const SLIDES = buildSlides(data);
  const isLast = slide === SLIDE_COUNT - 1;

  return (
    <div
      className={`min-h-screen relative overflow-hidden bg-gradient-to-br ${SLIDE_BG[slide % SLIDE_BG.length]} transition-colors duration-700`}
      onClick={next}
      data-testid="wrapped-page"
    >
      {/* Grain overlay for depth */}
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-overlay"
           style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27200%27 height=%27200%27><filter id=%27n%27><feTurbulence baseFrequency=%270.9%27/></filter><rect width=%27200%27 height=%27200%27 filter=%27url(%23n)%27/></svg>")' }}
      />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4">
        {/* Progress strips */}
        <div className="flex gap-1 flex-1 max-w-md mx-auto">
          {SLIDES.map((_, i) => (
            <div key={`progress-strip-${i}`} className="h-1 flex-1 rounded-full bg-white/15 overflow-hidden">
              <div
                className="h-full bg-white"
                style={{
                  width: i < slide ? '100%' : i === slide ? '50%' : '0%',
                  transition: i === slide ? 'width 4.5s linear' : 'width 0.3s',
                }}
              />
            </div>
          ))}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); navigate('/app'); }}
          className="ml-3 text-white/60 hover:text-white p-2"
          data-testid="wrapped-close-btn"
          aria-label="Close Wrapped"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tap zones — left = back, right = next */}
      <button
        type="button"
        className="absolute left-0 top-0 bottom-0 w-1/3 z-10"
        onClick={(e) => { e.stopPropagation(); back(); }}
        aria-label="Previous slide"
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={slide}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="min-h-screen flex flex-col items-center justify-center text-center px-6 relative z-0"
        >
          {SLIDES[slide]}
        </motion.div>
      </AnimatePresence>

      {/* Bottom CTA — only on last slide */}
      {isLast && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="absolute bottom-8 left-0 right-0 z-20 px-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-center gap-3 max-w-sm mx-auto">
            <p className="text-sm text-white/70">Share your Wrapped</p>
            <div className="flex gap-2.5 w-full">
              <button
                onClick={() => share('twitter')}
                className="flex-1 bg-white text-black hover:bg-white/90 py-3 px-4 rounded-2xl font-semibold flex items-center justify-center gap-2 transition"
                data-testid="wrapped-share-twitter"
              >
                <Twitter className="w-4 h-4" /> X
              </button>
              <button
                onClick={() => share('whatsapp')}
                className="flex-1 bg-white/10 text-white hover:bg-white/20 py-3 px-4 rounded-2xl font-semibold flex items-center justify-center gap-2 transition border border-white/20"
                data-testid="wrapped-share-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
              <button
                onClick={() => share('copy')}
                className="flex-1 bg-white/10 text-white hover:bg-white/20 py-3 px-4 rounded-2xl font-semibold flex items-center justify-center gap-2 transition border border-white/20"
                data-testid="wrapped-share-copy"
              >
                <Share2 className="w-4 h-4" /> Copy
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ─── Slides ────────────────────────────────────────────────────

function buildSlides(d) {
  const { user, year, focus_hours, focus_sessions, flashcard_reviews,
          flashcard_accuracy_pct, lectures, top_subject,
          longest_streak_days, percentile } = d;
  return [
    <Slide
      key="intro"
      kicker={`${year} Wrapped`}
      title={`Hey ${user.first_name},`}
      subtitle="Let's look at the year you just had."
      icon={Sparkles}
      testId="wrapped-slide-intro"
    />,
    <Slide
      key="focus"
      kicker="You stayed in the chair"
      title={focus_hours >= 1 ? `${focus_hours}h` : `${d.focus_minutes}m`}
      subtitle={`of pure focus, across ${focus_sessions} sessions.`}
      icon={Clock}
      testId="wrapped-slide-focus"
    />,
    <Slide
      key="lectures"
      kicker="You showed up to class"
      title={String(lectures)}
      subtitle={`lecture${lectures === 1 ? '' : 's'} captured and processed.`}
      icon={BookOpen}
      testId="wrapped-slide-lectures"
    />,
    <Slide
      key="streak"
      kicker="Your longest streak"
      title={`${longest_streak_days} day${longest_streak_days === 1 ? '' : 's'}`}
      subtitle={longest_streak_days >= 7 ? 'Discipline is a habit, not a feeling.' : 'Next year, longer.'}
      icon={Flame}
      testId="wrapped-slide-streak"
    />,
    <Slide
      key="flashcards"
      kicker="Knowledge retention"
      title={String(flashcard_reviews)}
      subtitle={flashcard_reviews > 0
        ? `flashcards reviewed · ${flashcard_accuracy_pct}% accuracy`
        : 'Try flashcards next year — your brain will thank you.'}
      icon={Brain}
      testId="wrapped-slide-flashcards"
    />,
    <Slide
      key="subject"
      kicker="Your obsession"
      title={top_subject?.name || 'No favourite yet'}
      subtitle={top_subject ? `${top_subject.lectures} lectures and counting.` : 'Pick a subject — we recommend the hardest one.'}
      icon={Trophy}
      testId="wrapped-slide-subject"
    />,
    <Slide
      key="percentile"
      kicker="You finished"
      title={`Top ${100 - percentile}%`}
      subtitle="of all iStudyPro students this year. Built different."
      icon={Sparkles}
      testId="wrapped-slide-percentile"
    />,
  ];
}

function Slide({ kicker, title, subtitle, icon: Icon, testId }) {
  return (
    <div className="text-white max-w-2xl" data-testid={testId}>
      {Icon && (
        <motion.div
          initial={{ scale: 0.6, rotate: -8 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 16 }}
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/12 backdrop-blur-md mb-6"
        >
          <Icon className="w-7 h-7" />
        </motion.div>
      )}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="text-base sm:text-lg text-white/70 uppercase tracking-[0.18em] font-medium mb-3"
      >
        {kicker}
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.5 }}
        className="text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight mb-5 leading-[1.05]"
        style={{ textShadow: '0 4px 30px rgba(0,0,0,0.3)' }}
      >
        {title}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-lg sm:text-xl text-white/85 leading-snug max-w-xl mx-auto"
      >
        {subtitle}
      </motion.p>
    </div>
  );
}
